import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useAuth } from '../auth/AuthContext.tsx';
import { convertMinor } from './convert.ts';
import { DISPLAY_CURRENCIES, formatInCurrency } from './formats.ts';
import { loadCachedRates, loadDisplayCurrency, saveCachedRates, saveDisplayCurrency } from './storage.ts';

export const NATIVE_CURRENCY = 'PGK';
const RATE_SOFT_TTL_MS = 24 * 60 * 60 * 1000;

export interface CurrencyContextValue {
  displayCurrency: string;
  setDisplayCurrency: (code: string) => void;
  status: 'idle' | 'loading' | 'ready' | 'error';
  rates: Record<string, number> | null;
  base: string | null;
  fetchedAt: string | null;
  stale: boolean;
  refresh: () => void;
  format: (minorNative: number, nativeCode: string | null) => string;
  convert: (minorNative: number, nativeCode: string | null) => number | null;
  isConverting: boolean;
  rateUnavailable: boolean;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

function isSoftStale(fetchedAt: string | null): boolean {
  if (fetchedAt === null) return true;
  const fetched = Date.parse(fetchedAt);
  return !Number.isFinite(fetched) || Date.now() - fetched > RATE_SOFT_TTL_MS;
}

export function CurrencyProvider({ children }: Readonly<{ children: ReactNode }>): React.JSX.Element {
  const { api, isAuthenticated } = useAuth();
  const initial = useMemo(() => ({ currency: loadDisplayCurrency() ?? NATIVE_CURRENCY, cached: loadCachedRates() }), []);
  const [currency, setCurrency] = useState(initial.currency);
  const [rates, setRates] = useState<Record<string, number> | null>(initial.cached?.rates ?? null);
  const [base, setBase] = useState<string | null>(initial.cached?.base ?? null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(initial.cached?.fetchedAt ?? null);
  const [status, setStatus] = useState<CurrencyContextValue['status']>('idle');
  const [stale, setStale] = useState(false);
  const inFlight = useRef(false);

  const refresh = useCallback((): void => {
    if (inFlight.current || !isAuthenticated) return;
    inFlight.current = true;
    setStatus('loading');
    void api.fx
      .getRates()
      .then((response) => {
        const cached = { base: response.base, rates: response.rates, fetchedAt: response.fetched_at };
        setRates(response.rates);
        setBase(response.base);
        setFetchedAt(response.fetched_at);
        setStale(response.stale);
        saveCachedRates(cached);
        setStatus('ready');
      })
      .catch(() => {
        setStatus('error');
        setStale(rates !== null);
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [api, isAuthenticated, rates]);

  useEffect(() => {
    if (status === 'idle' && currency !== NATIVE_CURRENCY && (rates === null || isSoftStale(fetchedAt))) refresh();
  }, [currency, fetchedAt, rates, refresh, status]);

  const setDisplayCurrency = useCallback((code: string): void => {
    if (!DISPLAY_CURRENCIES.some((currency) => currency.code === code)) return;
    setCurrency(code);
    if (code !== NATIVE_CURRENCY) setStatus('idle');
    saveDisplayCurrency(code);
  }, []);

  const convert = useCallback(
    (minorNative: number, nativeCode: string | null): number | null =>
      convertMinor(minorNative, nativeCode ?? NATIVE_CURRENCY, currency, rates),
    [currency, rates],
  );

  const format = useCallback(
    (minorNative: number, nativeCode: string | null): string => {
      const native = nativeCode ?? NATIVE_CURRENCY;
      const converted = convertMinor(minorNative, native, currency, rates);
      return converted === null ? formatInCurrency(minorNative, native) : formatInCurrency(converted, currency);
    },
    [currency, rates],
  );

  const isConverting = currency !== NATIVE_CURRENCY;
  const rateUnavailable = isConverting && (rates === null || typeof rates.AUD !== 'number');
  const value = useMemo<CurrencyContextValue>(
    () => ({ displayCurrency: currency, setDisplayCurrency, status, rates, base, fetchedAt, stale, refresh, format, convert, isConverting, rateUnavailable }),
    [currency, setDisplayCurrency, status, rates, base, fetchedAt, stale, refresh, format, convert, isConverting, rateUnavailable],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useDisplayCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (context === null) throw new Error('useDisplayCurrency must be used within a CurrencyProvider');
  return context;
}

export { CurrencyContext };
